import React, { useState, useEffect } from 'react';
import { EnquiryService, CourseService } from '../services/api';
import Modal from './common/Modal';
import ConfirmModal from './common/ConfirmModal';
import { getTerminology } from '../utils/terminology';
import { useSettings } from '../context/SettingsContext';
import { exportToExcel } from '../utils/exportHelper';

const Enquiries = () => {
  const { settings } = useSettings();
  const [enquiries, setEnquiries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const canExport = userRole === 'admin' || userRole === 'super_admin';

  const terminology = React.useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const studentTerm = terminology.student;
  const studentsTerm = terminology.students;
  const term = terminology.course;

  // View Mode: 'active' or 'converted'
  const [viewMode, setViewMode] = useState('active');

  // Confirmation Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToConvert, setItemToConvert] = useState(null);

  const [stats, setStats] = useState({ total: 0, new: 0, hot: 0, converted: 0 });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    course_interest: '',
    source: '',
    status: 'warm',
    reference_details: ''
  });

  // Table State
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  useEffect(() => {
    loadEnquiries();
    loadCourses();
  }, []);

  const loadEnquiries = async () => {
    try {
      const response = await EnquiryService.getAll();
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      }
      setEnquiries(data);
      calculateStats(data);
    } catch (error) {
      console.error("Error loading enquiries", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAll();
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      }
      setCourses(data);
    } catch (error) {
      console.error("Error loading courses", error);
    }
  };

  const calculateStats = (data) => {
    const total = data.length;
    const newEnq = data.filter(e => e.status === 'new' || e.status === 'warm').length;
    const hot = data.filter(e => e.status === 'hot').length;
    const converted = data.filter(e => e.status === 'converted').length;
    setStats({ total, new: newEnq, hot, converted });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await EnquiryService.create(formData);
      setShowModal(false);
      loadEnquiries();
      setFormData({ name: '', phone: '', course_interest: '', source: '', status: 'warm', reference_details: '' });
    } catch (error) {
      console.error("Error creating enquiry", error);
    } finally {
      setSaving(false);
    }
  };

  // --- Table Logic ---

  // 1. Filtering (View Mode + Search)
  const filteredData = enquiries.filter(item => {
    // View Mode Logic
    if (viewMode === 'active' && item.status === 'converted') return false;
    if (viewMode === 'converted' && item.status !== 'converted') return false;

    const searchTermLower = searchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(searchTermLower) ||
      item.phone?.includes(searchTermLower) ||
      item.course_interest?.toLowerCase().includes(searchTermLower)
    );
  });

  // 2. Sorting
  const sortedData = React.useMemo(() => {
    let sortableItems = [...filteredData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);

  // 3. Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Reset page on view mode change
  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, searchTerm]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (name) => {
    if (sortConfig.key !== name) return <i className="bi bi-arrow-down-up text-muted small ms-1" style={{ fontSize: '10px' }}></i>;
    if (sortConfig.direction === 'asc') return <i className="bi bi-sort-down-alt text-primary ms-1"></i>;
    return <i className="bi bi-sort-up text-primary ms-1"></i>;
  };


  const handleExport = () => {
    if (sortedData.length === 0) return;

    const exportData = sortedData.map(e => ({
      'Name': e.name,
      'Phone': e.phone,
      [term]: e.course_interest,
      'Source': e.source || 'N/A',
      'Status': e.status.toUpperCase(),
      'Created At': e.created_at ? e.created_at.split('T')[0] : '-',
      'Reference': e.reference_details || '-'
    }));

    exportToExcel(exportData, `Enquiries_Export_${new Date().toISOString().split('T')[0]}`);
  };

  const handleEdit = (enquiry) => {
    setFormData(enquiry);
    setShowModal(true);
  };

  const handleStatusClick = (enquiry) => {
    if (enquiry.status === 'converted') return;
    setItemToConvert(enquiry);
    setShowConfirm(true);
  };

  const confirmConversion = async () => {
    if (!itemToConvert) return;

    // Optimistic update
    const updatedEnquiries = enquiries.map(e =>
      e.id === itemToConvert.id ? { ...e, status: 'converted' } : e
    );
    setEnquiries(updatedEnquiries);
    calculateStats(updatedEnquiries);

    // API Call would go here
    // await EnquiryService.update(itemToConvert.id, { status: 'converted' });

    setShowConfirm(false);
    setItemToConvert(null);
  };

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="fw-bold">Enquiry Management</h2>
          <p className="text-muted">Manage leads and potential {studentsTerm.toLowerCase()}</p>
        </div>
        <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={() => {
          setFormData({ name: '', phone: '', course_interest: '', source: '', status: 'warm', reference_details: '' });
          setShowModal(true);
        }}>
          <i className="bi bi-plus-lg"></i> Add Enquiry
        </button>
      </div>

      {/* Iconic Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Total Enquiries</p>
            <h3 className="mb-0">{stats.total}</h3>
          </div>
          <div className="stat-icon bg-blue-light">
            <i className="bi bi-folder2-open"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Active/New</p>
            <h3 className="mb-0">{stats.new}</h3>
          </div>
          <div className="stat-icon bg-orange-light">
            <i className="bi bi-fire"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Hot Leads</p>
            <h3 className="mb-0">{stats.hot}</h3>
          </div>
          <div className="stat-icon bg-purple-light text-primary">
            <i className="bi bi-lightning-fill"></i>
          </div>
        </div>
        <div
          className={`stat-card cursor-pointer ${viewMode === 'converted' ? 'border-primary' : ''}`}
          onClick={() => setViewMode(viewMode === 'converted' ? 'active' : 'converted')}
          style={{ cursor: 'pointer', transition: 'all 0.2s' }}
        >
          <div className="stat-info">
            <p className={`mb-1  small fw-bold ${viewMode === 'converted' ? 'text-primary' : 'text-muted'}`}>
              Converted {viewMode === 'converted' && '(Viewing)'}
            </p>
            <h3 className="mb-0">{stats.converted}</h3>
          </div>
          <div className="stat-icon bg-green-light">
            <i className="bi bi-check-circle-fill"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">
        <div className="section-header px-4 pt-4 d-flex justify-content-between align-items-center mb-3 flex-wrap-mobile">
          <div className="d-flex align-items-center gap-3 flex-wrap-mobile">
            <h5 className="mb-0 fw-bold">Recent Enquiries</h5>
            <div className="bg-light p-1 rounded-pill d-flex overflow-auto">
              <button
                className={`btn btn-sm rounded-pill px-3 ${viewMode === 'active' ? 'btn-white shadow-sm fw-bold' : 'text-muted'}`}
                onClick={() => setViewMode('active')}
              >
                Active
              </button>
              <button
                className={`btn btn-sm rounded-pill px-3 ${viewMode === 'converted' ? 'btn-white shadow-sm fw-bold text-success' : 'text-muted'}`}
                onClick={() => setViewMode('converted')}
              >
                Converted
              </button>
            </div>
          </div>
          <div className="d-flex gap-3 align-items-center flex-wrap-mobile">
            {/* Improved Search Design */}
            <div className="position-relative w-100">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder="Search enquiries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ height: '40px' }}
              />
            </div>
            {canExport && (
              <button className="btn btn-outline-light text-dark border d-flex align-items-center gap-2 rounded-pill px-3" onClick={handleExport}>
                <i className="bi bi-download"></i> Export
              </button>
            )}
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead className="bg-white border-bottom">
              <tr>
                <th onClick={() => requestSort('name')} className="cursor-pointer ps-4" style={{ cursor: 'pointer' }}>
                  Name {getSortIcon('name')}
                </th>
                <th onClick={() => requestSort('phone')} style={{ cursor: 'pointer' }}>
                  Phone {getSortIcon('phone')}
                </th>
                <th onClick={() => requestSort('course_interest')} style={{ cursor: 'pointer' }}>
                  {term} {getSortIcon('course_interest')}
                </th>
                <th onClick={() => requestSort('source')} style={{ cursor: 'pointer' }}>
                  Source {getSortIcon('source')}
                </th>
                <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>
                  Status {getSortIcon('status')}
                </th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5 text-muted">Loading enquiries...</td></tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((enquiry) => (
                  <tr key={enquiry.id}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center gap-3">
                        <div className="avatar-sm bg-light text-primary d-flex align-items-center justify-content-center rounded-circle fw-bold" style={{ width: 40, height: 40, fontSize: '1.1rem' }}>
                          {enquiry.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="fw-semibold text-dark">{enquiry.name}</span>
                      </div>
                    </td>
                    <td>{enquiry.phone}</td>
                    <td>{enquiry.course_interest}</td>
                    <td><span className="badge bg-light text-dark border fw-normal px-3 py-2 rounded-pill">{enquiry.source || 'N/A'}</span></td>
                    <td>
                      <span className={`status-badge ${getStatusClass(enquiry.status)}`}>
                        {enquiry.status}
                      </span>
                    </td>
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-link text-secondary p-0" onClick={() => handleEdit(enquiry)} title="Edit" style={{ textDecoration: 'none' }}>
                          <i className="bi bi-pencil-square fs-5"></i>
                        </button>
                        {viewMode !== 'converted' && (
                          <button className="btn btn-link text-success p-0" onClick={() => handleStatusClick(enquiry)} title="Mark Converted" style={{ textDecoration: 'none' }}>
                            <i className="bi bi-check-circle-fill fs-5"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="text-center py-5 text-muted">
                  {viewMode === 'converted' ? 'No converted enquiries yet.' : 'No active enquiries found.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-top">
            <span className="text-muted small">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '32px', height: '32px' }}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              {/* Page Numbers */}
              <div className="d-flex gap-1">
                {[...Array(totalPages)].map((_, i) => {
                  const pageNumber = i + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        className={`btn btn-sm rounded-circle ${currentPage === pageNumber ? 'btn-primary text-white' : 'btn-white text-muted hover-bg-light'}`}
                        style={{ width: '32px', height: '32px', fontWeight: currentPage === pageNumber ? 'bold' : 'normal' }}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    (pageNumber === currentPage - 2 && pageNumber > 1) ||
                    (pageNumber === currentPage + 2 && pageNumber < totalPages)
                  ) {
                    return <span key={pageNumber} className="text-muted align-self-end small px-1">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '32px', height: '32px' }}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={formData.id ? "Edit Enquiry" : "Add New Enquiry"}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Name</label>
            <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label">Phone</label>
            <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label">{term} Interest</label>
            <select className="form-select" name="course_interest" value={formData.course_interest} onChange={handleInputChange} required>
              <option value="">Select {term}</option>
              {Array.isArray(courses) && courses.map(course => (
                <option key={course.id} value={course.name}>{course.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Source</label>
            <select className="form-select" name="source" value={formData.source} onChange={handleInputChange}>
              <option value="">Select Source</option>
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="Google">Google / Website</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Advertisement">Advertisement (Banner/Flyer)</option>
              <option value="Walk-in">Walk-in</option>
              <option value="Referral">Referral</option>
            </select>
          </div>

          {formData.source === 'Referral' && (
            <div className="mb-3 bg-light p-3 rounded">
              <label className="form-label text-primary">Reference Details</label>
              <input
                type="text"
                className="form-control"
                name="reference_details"
                placeholder="Who referred? (Name/Phone)"
                value={formData.reference_details || ''}
                onChange={handleInputChange}
              />
            </div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : 'Save Enquiry'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmConversion}
        title="Mark as Converted"
        message={`Are you sure you want to mark "${itemToConvert?.name}" as converted? \nThis will move the enquiry to the converted list and hide it from here.`}
        confirmText="Yes, Convert"
        type="success"
      />
    </div>
  );
};

const getStatusClass = (status) => {
  switch (status) {
    case 'hot': return 'status-cutting';
    case 'warm': return 'bg-orange-light text-orange';
    case 'cold': return 'bg-secondary text-white';
    case 'converted': return 'status-ready';
    default: return 'status-new';
  }
};

export default Enquiries;
